import React, { useEffect, useState } from "react";
import { Card, Button, Tag, Space, message } from "antd";
import { CheckCircleFilled, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";

// One policy, with an "I have read and understood" button when it asks for one.
const PolicyView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request } = useRequest();
  const [policy, setPolicy] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () =>
    request({
      url: `${apiPath.policies}/${id}`,
      method: "GET",
      onSuccess: (res) => setPolicy(res?.data || null),
      onError: () => setMissing(true),
    });
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const acknowledge = () => {
    setBusy(true);
    request({
      url: `${apiPath.policies}/${id}/acknowledge`,
      method: "POST",
      onSuccess: () => {
        setBusy(false);
        message.success("Thank you, your acknowledgement is recorded");
        load();
      },
      onError: (err) => {
        setBusy(false);
        message.error(err?.response?.data?.message || "Could not record your acknowledgement");
      },
    });
  };

  if (missing) return <PageTransition><Card><EmptyState message="This policy is not available to you" actionLabel="Back to policies" onAction={() => navigate("/policies")} /></Card></PageTransition>;
  if (!policy) return <PageTransition>{null}</PageTransition>;

  return (
    <PageTransition>
      <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate("/policies")} style={{ paddingLeft: 0 }}>
        All policies
      </Button>
      <Card style={{ borderRadius: 14 }} bodyStyle={{ padding: "24px 28px" }}>
        <Space size={[6, 6]} wrap style={{ marginBottom: 8 }}>
          <Tag color="purple" style={{ margin: 0 }}>{policy.function}</Tag>
          {policy.policyType?.name ? <Tag style={{ margin: 0 }}>{policy.policyType.name}</Tag> : null}
          <Tag style={{ margin: 0 }}>Version {policy.version}</Tag>
        </Space>
        <h1 style={{ fontSize: 24, margin: "0 0 4px" }}>{policy.title}</h1>
        <div style={{ color: "#6B7280", fontSize: 13, marginBottom: 18 }}>
          {policy.effectiveDate ? `Effective from ${moment(policy.effectiveDate).format("DD MMM YYYY")}` : policy.publishedAt ? `Published ${moment(policy.publishedAt).format("DD MMM YYYY")}` : ""}
        </div>
        <div className="policy-body" dangerouslySetInnerHTML={{ __html: policy.content }} />
      </Card>
      {policy.requiresAcknowledgement ? (
        <Card style={{ marginTop: 16, borderRadius: 14 }} bodyStyle={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          {policy.acknowledged ? (
            <div style={{ color: "#0F766E", fontWeight: 600 }}>
              <CheckCircleFilled /> You acknowledged this policy{policy.acknowledgedAt ? ` on ${moment(policy.acknowledgedAt).format("DD MMM YYYY")}` : ""}.
            </div>
          ) : (
            <>
              <div style={{ flex: 1, minWidth: 220 }}>
                <b>Please confirm</b>
                <div style={{ color: "#6B7280", fontSize: 13 }}>Your company asks you to confirm that you have read and understood this policy.</div>
              </div>
              <Button type="primary" loading={busy} onClick={acknowledge}>
                I have read and understood
              </Button>
            </>
          )}
        </Card>
      ) : null}
    </PageTransition>
  );
};

export default PolicyView;
